'use client'
import React, { useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { BrandLogo } from '@/components/icons'
import NextLink from 'next/link'
import { RegisterForm } from '@/components/ui/common-form-signup/RegisterForm'
import { useSelector } from 'react-redux'
import authSelector from '@/redux/selectors/auth'
import { authRedirectPathSwitchObj } from '@/utils/auth'
import { UserRole } from '@/types/firebase'
import { safeNextPath } from '@/utils/safe-next-path'

function RegisterPage() {
  const router = useRouter()
  const params = useSearchParams()
  const courseId = params?.get('courseId')
  const topicId = params?.get('topicId')
  const lessonId = params?.get('lessonId')
  const paperId = params?.get('paperId')
  const subscriptionPlanId = params?.get('subscriptionPlanId')
  // WP card context-carrying flow: the question (or topic) the visitor was
  // on, plus optionally a #h= hash carrying the pre-login Axi chat history.
  const demoLesson = params?.get('lesson')
  const demoTopic = params?.get('topic')
  const demoSubject = params?.get('subject')
  // WP exam-question / tutorial pages: the question ref (e.g. "Edexcel C3
  // June 2014 Q5") so the chat's back link can return to that paper.
  const demoRef = params?.get('ref')
  // General-page card variant: no exam question - return to the Axi tutor
  // chat with the pre-login conversation (#h= hash) carried over.
  const demoChat = params?.get('chat')
  // Page the visitor was on before login (withAuth adds ?next=)
  const nextPath = safeNextPath(params?.get('next'))

  const buildDemoSeedPath = (): string | null => {
    const hash =
      typeof window !== 'undefined' ? window.location.hash : ''

    if (demoChat) {
      // Keep the card's topic context on the handoff: with no #h history
      // the assistant home seeds a chat from it instead of showing a dead
      // empty state.
      const chatParams = new URLSearchParams()

      if (demoTopic) {
        chatParams.set('topic', demoTopic)
      }
      if (demoSubject) {
        chatParams.set('subject', demoSubject)
      }
      if (demoRef) {
        chatParams.set('ref', demoRef)
      }
      if (demoLesson) {
        chatParams.set('lesson', demoLesson)
      }

      const chatQuery = chatParams.toString()

      return `/student/ai-assistant${
        chatQuery ? `?${chatQuery}` : ''
      }${hash}`
    }

    if (!demoLesson && !demoTopic) {
      return null
    }

    const seedParams = new URLSearchParams()

    if (demoLesson) {
      seedParams.set('lesson', demoLesson)
    }
    if (demoTopic) {
      seedParams.set('topic', demoTopic)
    }
    if (demoSubject) {
      seedParams.set('subject', demoSubject)
    }
    if (demoRef) {
      seedParams.set('ref', demoRef)
    }

    return `/embedded/demo-dynamic-papers?${seedParams.toString()}${hash}`
  }
  const { user } = useSelector(authSelector)

  useEffect(() => {
    if (!user) {
      return
    }

    // Context-carrying demo flow: land back on the same question, signed in
    const demoPath = buildDemoSeedPath()

    if (demoPath) {
      router.push(demoPath)

      return
    }

    if (nextPath && user.role?.toLowerCase() === UserRole.Student) {
      router.push(nextPath)

      return
    }

    let path = '/login'

    // redirect to embedded page if user is already logged in
    if (!!subscriptionPlanId) {
      path += `?subscriptionPlanId=${subscriptionPlanId}`
      router.push(path)

      return
    }

    const pathToRedirect = authRedirectPathSwitchObj[user.role]

    if (pathToRedirect) {
      path = pathToRedirect
    }

    if (user.role === UserRole.Tutor && user.schoolRole === 'schoolAdmin') {
      path = '/tutors-dashboard'
    }

    if (user.role.toLowerCase() === 'schooladmin') {
      path = 'school/members'
    }

    if (courseId) {
      path += `?withredirect=true&courseId=${courseId}`

      if (topicId) {
        path += `&topicId=${topicId}`

        if (lessonId) {
          path += `&lessonId=${lessonId}`
        }
      }
    }
    if (paperId) {
      path += `?paperId=${paperId}`
    }
    router.push(path)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseId,
    lessonId,
    paperId,
    router,
    subscriptionPlanId,
    topicId,
    user,
    nextPath,
  ])

  return (
    <div className="relative min-h-screen py-12 sm:px-6 lg:px-8 bg-v8-paper flex flex-col justify-center items-center">
      <div className="w-full sm:max-w-md">
        <div className="mx-auto w-full flex flex-col justify-center items-center shrink-0">
          <NextLink href="https://examsolutions.net" className="flex">
            <BrandLogo />
          </NextLink>

          <h2 className="mt-4 text-center text-[30px] font-v8 font-extrabold tracking-tight text-v8-ink">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-v8-mut">
            Practice questions, AI marking, and progress tracking
          </p>

          {/* The promise in writing before the form (free-journey concept):
              the real free meters, exactly as the credits system enforces. */}
          <div className="mt-4 w-full rounded-xl border border-v8-line bg-v8-blue/5 px-4 py-3 text-center">
            <div className="text-sm font-semibold text-v8-ink">
              5 free credits a month, per feature
            </div>
            <div className="mt-0.5 text-xs text-v8-mut">
              Practice questions - guided courses - paper uploads - paper
              builds. Shown before you spend them. No card, ever.
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col justify-center mt-5 sm:mt-0 item-center">
          <div className="w-full mt-2 sm:mx-auto md:max-w-md">
            <div className="w-full px-4 py-6 bg-white border border-v8-line shadow-v8-card sm:rounded-[20px] sm:px-[40px] relative">
              {/* Register Form */}
              <RegisterForm
                {...(buildDemoSeedPath()
                  ? { onSuccessRedirectPath: buildDemoSeedPath() as string }
                  : {})}
              />

              {/* Login Redirect */}
              <div className="text-sm font-medium text-v8-ink flex justify-center pt-4">
                {'Already have an account?'}
                <button
                  onClick={() => {
                    let path = '/login'

                    // handle case when opened as exam-pack embedded page
                    if (!!subscriptionPlanId) {
                      path += `?subscriptionPlanId=${subscriptionPlanId}`

                      router.push(path)
                      return
                    }

                    if (courseId) {
                      path += `?courseId=${courseId}`
                      if (topicId) {
                        path += `&topicId=${topicId}`
                        if (lessonId) {
                          path += `&lessonId=${lessonId}`
                        }
                      }
                    }
                    if (paperId) {
                      path += `?paperId=${paperId}`
                    }
                    const loginParams = new URLSearchParams()

                    if (demoLesson) {
                      loginParams.set('lesson', demoLesson)
                    }
                    if (demoTopic) {
                      loginParams.set('topic', demoTopic)
                    }
                    if (demoSubject) {
                      loginParams.set('subject', demoSubject)
                    }
                    if (demoRef) {
                      loginParams.set('ref', demoRef)
                    }
                    if (demoChat) {
                      loginParams.set('chat', demoChat)
                    }

                    if (nextPath) {
                      loginParams.set('next', nextPath)
                    }

                    const demoQuery = loginParams.toString()

                    if (demoQuery) {
                      path += `${path.includes('?') ? '&' : '?'}${demoQuery}`
                    }

                    router.push(
                      path +
                        (typeof window !== 'undefined'
                          ? window.location.hash
                          : ''),
                    )
                  }}
                  className="text-v8-blue outline-none pl-3"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
